import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import {
  getOfferDetailsConfig,
  getSectionedOfferDetails,
} from './offerDetailsConfig';
import { RequisitionDetailsList } from '@/components/notifications/list/requisition-notifications/components';
import DOMPurify from 'dompurify';
import { CenterSpinner } from '@/components/ats/loadders';
import { useQuery } from '@tanstack/react-query';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { MarkdownSubtext } from '@/utils/MarkdownSubtext';

interface OfferDetailsSectionProps {
  currentOffer: any;
  // Notification-style layout: fields grouped by section with headers (approval modal).
  sectioned?: boolean;
}

// Rich-text values (tables/annexure) can exceed the drawer width — scroll
// horizontally instead of clipping. (ENG-3608)
const renderValue = (value: string) => {
  return (
    <Box
      data-test-id="offer-detail-rich-value"
      flex="1"
      minW="0"
      maxW="100%"
      overflowX="auto"
    >
      <KText size="sm-medium" color="secondary-700">
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
      </KText>
    </Box>
  );
};

const isRichText = (value: string): boolean =>
  /<[a-z][\s\S]*>/i.test(value || '');

// RequisitionDetailsList truncates string values but renders React nodes as-is;
// pass rich-text (HTML) values as a sanitized node, plain values as strings.
const toListValue = (value: string) =>
  isRichText(value) ? renderValue(value) : value;

export const OfferDetailsSection = ({
  currentOffer,
  sectioned = false,
}: OfferDetailsSectionProps) => {
  const {
    data: formData = {},
    isLoading: isFormDataLoading,
    isFetching: isFormDataFetching,
  } = useQuery({
    queryKey: ['offer/custom_fields', 'offer'],
    queryFn: async () => {
      const response = await fetchSelectOptions({
        endPoint: 'ats_custom_fields/form_fields?subject_type=ats_offer',
        method: 'get',
      });
      return response || {};
    },
    staleTime: 0,
  });

  if (isFormDataLoading || isFormDataFetching) {
    return (
      <Flex justifyContent="center" alignItems="center" p="24px">
        <CenterSpinner h="200px" />
      </Flex>
    );
  }

  const staticFieldSections = formData?.ats_form_template?.static_fields || [];
  const customFields = formData?.ats_form_fields || [];

  if (sectioned) {
    const sections = getSectionedOfferDetails(
      currentOffer,
      staticFieldSections,
      customFields
    );
    return (
      <Flex flexDir="column" gap="24px" p="16px">
        {sections.map((section) => (
          <Flex flexDir="column" gap="8px" key={section.sectionId}>
            <KText
              variant="heading_4"
              color="secondary-900"
              fontWeight="semiBold"
            >
              {section.sectionLabel}
            </KText>
            <Box borderBottom="1px solid" borderColor="border-color" />
            <RequisitionDetailsList
              p="0"
              data={section.fields.map(({ label, value }) => ({
                label,
                value: toListValue(value),
                // Rich-text values stack on their own full-width line. (ENG-3608)
                stack: isRichText(value),
              }))}
            />
          </Flex>
        ))}
      </Flex>
    );
  }

  const detailsData = getOfferDetailsConfig(
    currentOffer,
    staticFieldSections,
    customFields
  );

  return (
    <Flex flexDir="column" gap="12px" p="16px">
      {detailsData.map(({ label, value, description }, index) => (
        <Flex key={index} flexDir="column" gap="4px">
          <KText size="sm-semi-bold" color="secondary-700">
            {label}
          </KText>
          {description && (
            <MarkdownSubtext
              markdown={description}
              bodyColor="secondary-500"
              minW={0}
              bg="transparent"
              p={0}
              m={0}
            />
          )}
          {renderValue(value)}
        </Flex>
      ))}
    </Flex>
  );
};
